import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid-premium";
import { capitalize } from "lodash";

import { BusinessUnitToggle, GeoViewToggle, MetricType } from "@/shared/types";
import { formatProductHierarchy } from "@/Utils/formatProductHierarchy";
import { formatCurrencyValue, formatUnitValue } from "@/Utils/formatters";
import { Layer } from "@/Utils/hierarchy";

interface FinancialReviewData {
  id: string;
  brand: string;
  geography?: string;
  category?: string;
  product?: string;
  source?: string;
  business_unit?: string;
  contribution: {
    [key: string]: number;
  };
  units: {
    [key: string]: number;
  };
}

export const getColumnDefinitions = (
  activeColumns: string[],
  options: {
    currencySymbol: string;
    formatCurrency: (val: number) => string;
    businessUnitToggle: BusinessUnitToggle;
    geoView?: GeoViewToggle;
    metricType: MetricType;
    mapDefaultNodeName: (name: string) => string;
    getLayerLabel: (layer: Layer) => string;
    salesUnitsLabel: string;
  }
) => {
  const {
    formatCurrency,
    currencySymbol,
    businessUnitToggle,
    geoView,
    metricType,
    mapDefaultNodeName,
    getLayerLabel,
    salesUnitsLabel,
  } = options;

  const columns: GridColDef<FinancialReviewData>[] = [
    {
      field: "business_unit",
      headerName: "Business Unit",
      valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) => row.business_unit,
      valueFormatter: ({ value }: { value: string }) => mapDefaultNodeName(value || ""),
      sortable: true,
      hideable: !activeColumns.includes("business_unit"),
      type: "string",
      flex: 1,
    },

    // Geography column - only show when geoView is split
    ...(geoView === GeoViewToggle.SPLIT
      ? [
          {
            field: "geography",
            headerName: "Geography",
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.geography || "",
            valueFormatter: ({ value }: { value: string }) => mapDefaultNodeName(value || ""),
            sortable: true,
            hideable: !activeColumns.includes("geography"),
            type: "string",
            flex: 1,
          },
        ]
      : []),

    // Business Unit fields (only when businessUnitToggle is split)
    ...(businessUnitToggle === BusinessUnitToggle.SPLIT
      ? [
          {
            field: "product_hierarchy",
            headerName: "Products",
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) => {
              const { brand, category, product } = row;
              return formatProductHierarchy(brand, category, product);
            },
            sortable: true,
            hideable: !activeColumns.includes("product_hierarchy"),
            flex: 1,
          },
        ]
      : []),

    // Revenue fields (shown only when metricType is SALES_REVENUE)
    ...(metricType === MetricType.SALES_REVENUE
      ? [
          {
            field: "base_sale",
            headerName: `Base Sale (${currencySymbol})`,
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.contribution?.base_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) =>
              formatCurrencyValue(value, formatCurrency),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("base_sale"),
          },
          {
            field: "brand_marketing_sale",
            headerName: `${getLayerLabel(Layer.Brand)} Marketing Sale (${currencySymbol})`,
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.contribution?.brand_marketing_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) =>
              formatCurrencyValue(value, formatCurrency),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("brand_marketing_sale"),
          },
          {
            field: "category_marketing_sale",
            headerName: `${getLayerLabel(Layer.Category)} Marketing Sale (${currencySymbol})`,
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.contribution?.category_marketing_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) =>
              formatCurrencyValue(value, formatCurrency),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("category_marketing_sale"),
          },

          {
            field: "earned_sale",
            headerName: `Earned Media Sale (${currencySymbol})`,
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.contribution?.earned_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) =>
              formatCurrencyValue(value, formatCurrency),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("earned_sale"),
          },
          {
            field: "media_sale",
            headerName: `${getLayerLabel(Layer.Product)} Marketing Sale (${currencySymbol})`,
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.contribution?.media_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) =>
              formatCurrencyValue(value, formatCurrency),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("media_sale"),
          },
          {
            field: "trade_sale",
            headerName: `Trade Sales (${currencySymbol})`,
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.contribution?.trade_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) =>
              formatCurrencyValue(value, formatCurrency),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("trade_sale"),
          },
          {
            field: "owned_sale",
            headerName: `Owned Sale (${currencySymbol})`,
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.contribution?.owned_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) =>
              formatCurrencyValue(value, formatCurrency),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("owned_sale"),
          },
          {
            field: "price_sale",
            headerName: `Price Sale (${currencySymbol})`,
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.contribution?.price_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) =>
              formatCurrencyValue(value, formatCurrency),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("price_sale"),
          },
          {
            field: "control_sale",
            headerName: `External Factors (${currencySymbol})`,
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.contribution?.control_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) =>
              formatCurrencyValue(value, formatCurrency),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("control_sale"),
          },
          {
            field: "error_sale",
            headerName: `Unattributed Sales (${currencySymbol})`,
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.contribution?.error_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) =>
              formatCurrencyValue(value, formatCurrency),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("error_sale"),
          },
          {
            field: "total_sales",
            headerName: `Total Sales (${currencySymbol})`,
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.contribution?.total ?? 0,
            valueFormatter: ({ value }: { value: number }) =>
              formatCurrencyValue(value, formatCurrency),
            sortable: true,
          },
        ]
      : []),

    // Units fields (shown only when metricType is SALES_UNITS)
    ...(metricType === MetricType.SALES_UNITS
      ? [
          {
            field: "base_sale_units",
            headerName: "Base Sale (Units)",
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.units?.base_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) => formatUnitValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("base_sale_units"),
          },
          {
            field: "brand_marketing_sale_units",
            headerName: "Brand Marketing Sale (Units)",
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.units?.brand_marketing_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) => formatUnitValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("brand_marketing_sale_units"),
          },
          {
            field: "category_marketing_sale_units",
            headerName: "Category Marketing Sale (Units)",
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.units?.category_marketing_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) => formatUnitValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("category_marketing_sale_units"),
          },
          {
            field: "media_sale_units",
            headerName: "Media Sale (Units)",
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.units?.media_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) => formatUnitValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("media_sale_units"),
          },
          {
            field: "trade_sale_units",
            headerName: `Trade Sale (${capitalize(salesUnitsLabel)})`,
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.units?.trade_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) => formatUnitValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("trade_sale_units"),
          },
          {
            field: "owned_sale_units",
            headerName: "Owned Sale (Units)",
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.units?.owned_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) => formatUnitValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("owned_sale_units"),
          },
          {
            field: "price_sale_units",
            headerName: "Price Sale (Units)",
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.units?.price_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) => formatUnitValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("price_sale_units"),
          },
          {
            field: "control_sale_units",
            headerName: "Control Sale (Units)",
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.units?.control_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) => formatUnitValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("control_sale_units"),
          },
          {
            field: "error_sale_units",
            headerName: "Error Sale (Units)",
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.units?.error_sale ?? 0,
            valueFormatter: ({ value }: { value: number }) => formatUnitValue(value),
            sortable: true,
            type: "number",
            hideable: !activeColumns.includes("error_sale_units"),
          },
          {
            field: "total_sales_units",
            headerName: `Total Sales (${capitalize(salesUnitsLabel)})`,
            valueGetter: ({ row }: GridValueGetterParams<FinancialReviewData>) =>
              row.units?.total ?? 0,
            valueFormatter: ({ value }: { value: number }) => formatUnitValue(value),
            sortable: true,
          },
        ]
      : []),
  ];

  return columns;
};
